import { FormbrewError } from "./errors.ts";
import { getCheckboxGroupValidationMessage } from "./form.ts";
import type { FormbrewStatusRole } from "./renderer.types.ts";
import { renderField } from "./rendering/registry.ts";
import type { PublicFormDefinition, PublicFormField } from "./types.ts";

export const createRenderedForm = (
  definition: PublicFormDefinition,
  instanceId: string,
): HTMLFormElement => {
  const form = document.createElement("form");
  form.className = "fb-form";
  form.noValidate = false;

  const fieldsById = new Map<string, PublicFormField>(
    definition.fields.map((field) => [field.id, field]),
  );

  assertConfirmTargets(definition.fields, fieldsById);

  const context = { fieldsById, instanceId };

  // Hidden fields render first, exactly once, as bare inputs ahead of the
  // visible layout so their untrusted values submit without disturbing the flow.
  for (const field of definition.fields) {
    if (formFieldCapabilities[field.type].renderPlacement === "hidden") {
      form.append(renderField(field, context));
    }
  }

  for (const field of definition.fields) {
    if (formFieldCapabilities[field.type].renderPlacement === "layout") {
      form.append(renderField(field, context));
    }
  }

  form.append(createHoneypot(definition.honeypotFieldName, instanceId));

  const submit = document.createElement("button");
  submit.className = "fb-submit";
  submit.type = "submit";
  submit.textContent = definition.submitButtonLabel;
  form.append(submit);

  const status = createStatusElement("", "status", "fb-status");
  status.setAttribute("aria-live", "polite");
  form.append(status);

  return form;
};

// Field types a CONFIRM control may mirror. Anything else is a configuration
// error the managed renderer refuses to mount rather than guess a control for.
const CONFIRM_TARGET_TYPES = new Set<PublicFormField["type"]>([
  "TEXT",
  "EMAIL",
  "PHONE",
  "WEBSITE",
  "NUMBER",
  "DATETIME",
]);

const assertConfirmTargets = (
  fields: PublicFormField[],
  fieldsById: ReadonlyMap<string, PublicFormField>,
): void => {
  for (const field of fields) {
    if (field.type !== "CONFIRM") {
      continue;
    }

    const target = fieldsById.get(field.configJson.targetFieldId);

    if (!target || target.id === field.id) {
      throw new FormbrewError(`Confirm field "${field.key}" references a missing target field.`, {
        code: "ConfigurationError",
      });
    }

    if (!CONFIRM_TARGET_TYPES.has(target.type)) {
      throw new FormbrewError(`Confirm field "${field.key}" targets an unsupported field type.`, {
        code: "ConfigurationError",
      });
    }
  }
};

export const createStatusElement = (
  text: string,
  role: FormbrewStatusRole,
  className: string,
): HTMLElement => {
  const status = document.createElement("p");
  status.className = className;
  status.role = role;
  status.textContent = text;

  return status;
};

export const resolveRendererTarget = (target: string | HTMLElement): HTMLElement => {
  let element: HTMLElement | null = null;

  if (typeof target === "string") {
    element = document.querySelector<HTMLElement>(target);
  } else {
    element = target;
  }

  if (!(element instanceof HTMLElement)) {
    throw new FormbrewError("The Formbrew form target was not found.", {
      code: "ConfigurationError",
    });
  }

  return element;
};

export const synchronizeChoiceGroups = (form: HTMLFormElement, fields: PublicFormField[]): void => {
  for (const field of fields) {
    if (field.type !== "CHECKBOX_GROUP") {
      continue;
    }

    const selector = `input[name="${escapeSelector(field.key)}"]`;
    const inputs = Array.from(form.querySelectorAll<HTMLInputElement>(selector));

    if (inputs.length === 0) {
      continue;
    }

    const selectedCount = inputs.filter((input) => input.checked).length;
    const message = getCheckboxGroupValidationMessage(field, selectedCount);

    inputs[0]?.setCustomValidity(message);
  }
};

const createHoneypot = (name: string, instanceId: string): HTMLElement => {
  const wrapper = document.createElement("div");
  wrapper.className = "fb-honeypot";
  wrapper.setAttribute("aria-hidden", "true");

  const label = document.createElement("label");
  label.htmlFor = `${instanceId}-honeypot`;
  label.textContent = "Leave this field blank";

  const input = document.createElement("input");
  input.autocomplete = "off";
  input.id = `${instanceId}-honeypot`;
  input.name = name;
  input.tabIndex = -1;
  input.type = "text";

  label.append(input);
  wrapper.append(label);

  return wrapper;
};

const escapeSelector = (value: string): string => {
  if (globalThis.CSS?.escape) {
    return globalThis.CSS.escape(value);
  }

  return value.replace(/["\\]/g, "\\$&");
};

import { formFieldCapabilities } from "../../contracts/src/schemas/fields/field.capabilities.ts";
